/* Loupe – newsletter pop-up. Opens after a delay, on exit intent (desktop) or after scrolling, at most once every
   N days. Never auto-opens on the cart, in the theme editor or after another newsletter form was just sent; after its
   own form posts it reopens to show the success (or error) state. In the editor it shows while the section is selected. */
(() => {
  // the first scroll, tap, key or mouse move – rotation / pop-ups wait for it (idle tabs and page-speed tests see a calm page)
  const firstInput = (fn) => {
    if (window.Shopify && Shopify.designMode) return fn();
    const ev = ['pointerdown', 'pointermove', 'keydown', 'scroll', 'touchstart', 'wheel'];
    const go = () => { ev.forEach((e) => removeEventListener(e, go, true)); fn(); };
    ev.forEach((e) => addEventListener(e, go, { passive: true, capture: true }));
  };
  if (customElements.get('loupe-newsletter-popup')) return;

  const KEY = 'loupe:newsletter-popup';        // localStorage: { until: ms } – don't auto-open before then
  const SENT = 'loupe:newsletter-popup-sent';  // sessionStorage: time this pop-up's form was submitted
  const DAY = 864e5;
  const FOCUSABLE = 'a[href], button:not([disabled]), input:not([type=hidden]):not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

  const storage = (session) => { try { return session ? sessionStorage : localStorage; } catch (e) { return null; } };
  const get = (k, session) => { try { return storage(session).getItem(k); } catch (e) { return null; } };
  const set = (k, v, session) => { try { storage(session).setItem(k, v); } catch (e) { /* private mode / blocked */ } };
  const del = (k, session) => { try { storage(session).removeItem(k); } catch (e) { /* ignore */ } };
  const designMode = () => !!(window.Shopify && window.Shopify.designMode);

  // Shared scroll lock (same class theme.js uses); only release it when no other overlay is still open.
  const othersOpen = (self) => [...document.querySelectorAll('.drawer.is-open, .agev.is-open, .nlp.is-open')].some((el) => el !== self);
  const lock = (on, self) => {
    if (on) document.body.classList.add('is-locked');
    else if (!othersOpen(self)) document.body.classList.remove('is-locked');
  };

  class LoupeNewsletterPopup extends HTMLElement {
    connectedCallback() {
      this.dialog = this.querySelector('[role="dialog"]');
      this.form = this.querySelector('form');
      this.isOpen = false;

      this.addEventListener('click', (e) => {
        if (e.target.closest('[data-nlp-close]')) { e.preventDefault(); this.close(); return; }
        const copy = e.target.closest('[data-copy]');
        if (copy) this.copy(copy);
      });
      this.addEventListener('keydown', (e) => {
        if (!this.isOpen) return;
        if (e.key === 'Escape') { e.stopPropagation(); this.close(); }
        else if (e.key === 'Tab') this.trap(e);
      });
      if (this.form) this.form.addEventListener('submit', () => set(SENT, String(Date.now()), true));

      if (designMode()) return;   // the editor opens it via shopify:section:select only

      const state = (this.querySelector('[data-nlp-state]') || {}).dataset;
      const posted = new URLSearchParams(location.search).get('customer_posted') === 'true';
      const sentAt = +get(SENT, true) || 0;
      const mine = (this.form && location.hash === '#' + this.form.id) || (sentAt && Date.now() - sentAt < 10 * 60 * 1000);

      if (state && state.nlpState === 'success' && posted && mine) {
        del(SENT, true);
        this.remember(365);
        this.open();
        return;
      }
      if (state && state.nlpState === 'error' && mine) {
        del(SENT, true);
        this.open('input[type="email"]');
        return;
      }
      if (posted) { this.remember(365); return; }   // signed up through another newsletter form
      if (state) return;                            // another form's result is on screen – stay out of the way
      if (/\/(cart|checkouts?)(\/|$)/.test(location.pathname)) return;
      if (this.hasAttribute('data-subscribed')) return;
      if (this.seen()) return;
      this.arm();
    }

    disconnectedCallback() {
      this.disarm();
      if (this.isOpen) { this.isOpen = false; lock(false, this); }
    }

    /* ---------- triggers ---------- */
    arm() {
      const trigger = this.dataset.trigger;
      const delay = Math.max(0, +this.dataset.delay || 0) * 1000;
      const finePointer = matchMedia('(hover: hover) and (pointer: fine)').matches;

      if (trigger === 'exit' && finePointer) {
        this.onExit = (e) => { if (!e.relatedTarget && e.clientY <= 0) this.auto(); };
        this.armTimer = setTimeout(() => document.addEventListener('mouseout', this.onExit), 2000);
        return;
      }
      if (trigger === 'delay_scroll') {
        const root = document.documentElement;
        const max = () => root.scrollHeight - innerHeight;
        const pct = Math.min(100, Math.max(1, +this.dataset.scroll || 50)) / 100;
        let ticking = false;
        this.onScroll = () => {
          if (ticking) return;
          ticking = true;
          requestAnimationFrame(() => { ticking = false; if (max() > 40 && scrollY / Math.max(1, max()) >= pct) this.auto(); });
        };
        addEventListener('scroll', this.onScroll, { passive: true });
        this.timer = setTimeout(() => this.auto(), delay);
        return;
      }
      if (trigger === 'scroll') {
        const root = document.documentElement;
        const max = () => root.scrollHeight - innerHeight;
        if (max() > 40) {
          const pct = Math.min(100, Math.max(1, +this.dataset.scroll || 40)) / 100;
          let ticking = false;
          this.onScroll = () => {
            if (ticking) return;
            ticking = true;
            requestAnimationFrame(() => { ticking = false; if (scrollY / Math.max(1, max()) >= pct) this.auto(); });
          };
          addEventListener('scroll', this.onScroll, { passive: true });
          return;
        }
      }
      // delay – also the fallback for exit intent on touch screens and for pages too short to scroll
      firstInput(() => { if (!this.fired && !this.isOpen) this.timer = setTimeout(() => this.auto(), delay); });
    }

    disarm() {
      clearTimeout(this.timer); clearTimeout(this.armTimer); clearTimeout(this.retry);
      if (this.onExit) document.removeEventListener('mouseout', this.onExit);
      if (this.onScroll) removeEventListener('scroll', this.onScroll);
      this.onExit = this.onScroll = null;
    }

    auto() {
      if (this.isOpen || this.fired) return;
      // phones: never within the first 5 seconds of the visit
      const minMs = +this.dataset.mobileMin || 0;
      if (minMs && matchMedia('(max-width: 749px)').matches && performance.now() < minMs) {
        clearTimeout(this.retry);
        this.retry = setTimeout(() => this.auto(), minMs - performance.now() + 50);
        return;
      }
      this.disarm();
      // Wait while a drawer, the age gate or another overlay holds the page.
      if (document.body.classList.contains('is-locked') || othersOpen(this)) {
        this.retry = setTimeout(() => this.auto(), 4000);
        return;
      }
      this.fired = true;
      this.remember(Math.max(1, +this.dataset.days || 7));
      this.open();
    }

    seen() {
      try { const v = JSON.parse(get(KEY) || 'null'); return !!(v && v.until > Date.now()); } catch (e) { return false; }
    }

    remember(days) { set(KEY, JSON.stringify({ until: Date.now() + days * DAY })); }

    /* ---------- dialog ---------- */
    open(focusSelector, { focus = true } = {}) {
      if (this.isOpen) return;
      this.isOpen = true;
      this.opener = document.activeElement;
      this.classList.add('is-open'); this.dataset.opened = '';
      lock(true, this);
      if (!focus) return;
      requestAnimationFrame(() => {
        const target = (focusSelector && this.querySelector(focusSelector)) || this.dialog;
        target && target.focus({ preventScroll: true });
      });
    }

    close() {
      if (!this.isOpen) return;
      this.isOpen = false;
      this.classList.remove('is-open');
      lock(false, this);
      const o = this.opener;
      this.opener = null;
      if (o && o !== document.body && o.isConnected && typeof o.focus === 'function' && !designMode()) o.focus({ preventScroll: true });
    }

    trap(e) {
      const items = [...this.dialog.querySelectorAll(FOCUSABLE)].filter((el) => el.offsetParent !== null && !el.closest('[hidden], [inert]'));
      if (!items.length) { e.preventDefault(); return; }
      const first = items[0], last = items[items.length - 1];
      const active = document.activeElement;
      if (e.shiftKey && (active === first || active === this.dialog)) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && active === last) { e.preventDefault(); first.focus(); }
    }

    copy(btn) {
      const codeEl = this.querySelector('[data-code]');
      const status = this.querySelector('[data-copy-status]');
      if (!codeEl) return;
      const code = codeEl.textContent.trim();
      const done = () => {
        btn.textContent = btn.dataset.copied;
        if (status) status.textContent = btn.dataset.copied;
        clearTimeout(this.copyTimer);
        this.copyTimer = setTimeout(() => { btn.textContent = btn.dataset.label; if (status) status.textContent = ''; }, 2400);
      };
      const select = () => {
        const range = document.createRange();
        range.selectNodeContents(codeEl);
        const sel = getSelection(); sel.removeAllRanges(); sel.addRange(range);
      };
      if (navigator.clipboard && window.isSecureContext) navigator.clipboard.writeText(code).then(done, select);
      else {
        select();
        try { if (document.execCommand('copy')) done(); } catch (e) { /* selection stays so the visitor can copy */ }
      }
    }
  }
  customElements.define('loupe-newsletter-popup', LoupeNewsletterPopup);

  /* ---------- theme editor: show while the section is selected ---------- */
  const popupIn = (e) => (e.target && e.target.querySelector ? e.target.querySelector('loupe-newsletter-popup') : null);
  document.addEventListener('shopify:section:select', (e) => { const p = popupIn(e); p && p.open(null, { focus: false }); });
  document.addEventListener('shopify:section:load', (e) => { const p = popupIn(e); p && p.open(null, { focus: false }); });
  document.addEventListener('shopify:section:deselect', (e) => { const p = popupIn(e); p && p.close(); });
})();
